import { useTranslation } from "react-i18next";
import { useReadOnlySpeakerNotes } from "../../../../../queries/speakerNotes";
import ReadOnlyMarkdownViewer from "../../../../../components/ReadOnlyMarkdownViewer";

interface SpeakerNotesPanelProps {
  pdfId: string | undefined;
}

export default function SpeakerNotesPanel({ pdfId }: SpeakerNotesPanelProps) {
  const { t } = useTranslation();
  const {
    pages,
    activePage,
    activeIndex,
    loading,
    contentLoading,
    selectPage,
    selectNextPage,
    selectPrevPage,
  } = useReadOnlySpeakerNotes(pdfId ?? null);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-indigo-500 border-t-transparent" />
      </div>
    );
  }

  if (pages.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
          <i className="ri-sticky-note-line text-xl text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-500">
          {t("remote.no_notes")}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          {t("remote.notes_empty")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* 노트 페이지 네비게이션 */}
      <div className="flex items-center gap-2 mb-2 flex-shrink-0">
        <button
          onClick={selectPrevPage}
          disabled={activeIndex <= 0}
          className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 active:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 rounded-lg transition-all cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>
        <div className="flex-1 overflow-x-auto">
          <div className="flex gap-1.5">
            {pages.map((page) => (
              <button
                key={page.id}
                onClick={() => selectPage(page.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${
                  activePage?.id === page.id
                    ? "bg-amber-500 text-white shadow-sm"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {page.title}
              </button>
            ))}
          </div>
        </div>
        <button
          onClick={selectNextPage}
          disabled={activeIndex >= pages.length - 1}
          className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 active:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 rounded-lg transition-all cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>
      </div>

      {/* 노트 내용 */}
      <div className="flex-1 overflow-auto bg-gray-50 rounded-xl border border-gray-200">
        {contentLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-indigo-500 border-t-transparent" />
          </div>
        ) : activePage ? (
          <ReadOnlyMarkdownViewer content={activePage.content} />
        ) : null}
      </div>
    </div>
  );
}
